"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import type { JobsApplyQueryResult } from "@/sanity/schemas/lib/queries";

interface JobsApplySectionProps {
    sanityContent: JobsApplyQueryResult;
}

export function JobsApplySection({ sanityContent }: JobsApplySectionProps) {
    const [formLoaded, setFormLoaded] = useState(false);
    const formRef = useRef<HTMLDivElement>(null);
    const facts = sanityContent.facts ?? [];
    const startLabel = sanityContent.startLabel || sanityContent.title;

    useEffect(() => {
        if (formLoaded) {
            formRef.current?.focus();
        }
    }, [formLoaded]);

    return (
        <section id="bewerben" className="bg-light-btn py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 md:px-20">
                <div className="mx-auto max-w-2xl text-center">
                    <h2 className="mb-4 text-h2-mobile leading-120 font-semibold text-ink md:text-h2-desktop">
                        {sanityContent.title}
                    </h2>
                    <p className="text-subtitle leading-140 text-system-subtitle">
                        {sanityContent.lead}
                    </p>

                    {facts.length > 0 && (
                        <ul className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3">
                            {facts.map((fact) => (
                                <li
                                    key={fact._key}
                                    className="text-xs font-medium tracking-[0.16em] text-system-subtitle uppercase"
                                >
                                    {fact.label}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="mx-auto mt-12 max-w-4xl pb-28 md:pb-32">
                    {formLoaded ? (
                        <div ref={formRef} tabIndex={-1} className="focus-visible:outline-none">
                            <iframe
                                src={sanityContent.formUrl}
                                title={sanityContent.title}
                                loading="lazy"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allow="camera 'src'; microphone 'src'; autoplay 'src'; encrypted-media 'src'; fullscreen 'src'"
                                className="h-150 w-full rounded-3xl border-0 bg-white shadow-[0px_4px_3.9px_0px_#1045510F,0px_2px_26.2px_0px_#157FA217] md:h-125"
                            />
                        </div>
                    ) : (
                        <div className="flex h-150 w-full flex-col items-center justify-center gap-6 rounded-3xl border border-ink/10 bg-white px-6 text-center md:h-125">
                            {sanityContent.privacyNote && (
                                <p className="max-w-md text-base leading-140 text-system-subtitle">
                                    {sanityContent.privacyNote}
                                </p>
                            )}
                            <Button variant="dark-1" onClick={() => setFormLoaded(true)}>
                                {startLabel}
                            </Button>
                        </div>
                    )}

                    {sanityContent.note && (
                        <p className="mt-6 text-center text-sm leading-140 text-system-subtitle">
                            {sanityContent.note}
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
}
